import React, { useState, useEffect } from 'react';
import { Upload, Trash2, FileText, Loader2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { documentsAPI } from '../../services/api';

const DocumentManagement = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [newDoc, setNewDoc] = useState({ title: '', description: '', category: 'Technique' });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await documentsAPI.getAll();
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !newDoc.title) {
      toast.error('Veuillez remplir tous les champs et sélectionner un fichier');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', newDoc.title);
      formData.append('description', newDoc.description);
      formData.append('category', newDoc.category);

      await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document sauvegardé avec succès !');
      setNewDoc({ title: '', description: '', category: 'Technique' });
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      loadDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      await documentsAPI.delete(docId);
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <h2 className="text-2xl font-bold text-white">Gestion des Documents</h2>

      {/* Upload Form */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Ajouter un Document
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            value={newDoc.title}
            onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
            placeholder="Titre du document"
            className="bg-slate-700 border-slate-600 text-white"
          />

          <select
            value={newDoc.category}
            onChange={(e) => setNewDoc({ ...newDoc, category: e.target.value })}
            className="bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
          >
            <option value="Technique">Technique</option>
            <option value="Financier">Financier</option>
            <option value="Juridique">Juridique</option>
            <option value="Marketing">Marketing</option>
            <option value="Autre">Autre</option>
          </select>
        </div>

        <textarea
          value={newDoc.description}
          onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
          placeholder="Description du document"
          className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-white min-h-[80px]"
        />

        <div className="flex items-center gap-4">
          <input
            type="file"
            onChange={(e) => setSelectedFile(e.target.files[0])}
            className="text-gray-300"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          />
          <Button
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Upload en cours...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Uploader et Sauvegarder
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-gray-400 mt-2">
          ℹ️ Le document sera automatiquement sauvegardé après l'upload
        </p>
      </div>

      {/* Documents List */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white">Documents ({documents.length})</h3>

        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <FileText className="w-8 h-8 text-blue-400" />
                <div>
                  <h4 className="text-white font-medium">{doc.title}</h4>
                  <p className="text-gray-400 text-sm">{doc.description}</p>
                  <span className="text-xs text-gray-500">{doc.category}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(doc.url, '_blank')}
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(doc.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}

          {documents.length === 0 && (
            <p className="text-gray-400 text-center py-8">Aucun document disponible</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentManagement;
